<template>
  <div class="quote-live-card">
    <div class="card-title-row">
      <div class="card-title-label">
        <v-icon small color="rgba(255,255,255,0.55)" class="tw-mr-1">mdi-airplane</v-icon>
        Quote summary
      </div>
      <span v-if="priceCalculated && !pricing" class="status-badge">Live</span>
      <span v-else-if="pricing || quoteReady" class="status-badge status-badge--pending">Updating…</span>
      <span v-else class="status-badge status-badge--pending">Pending</span>
    </div>

    <div v-if="!priceCalculated && !pricing" class="quote-placeholder">
      <v-icon large color="rgba(255,255,255,0.4)">mdi-earth</v-icon>
      <p v-if="gateReason">{{ gateReason }}</p>
      <p v-else-if="quoteReady">Fetching your premium…</p>
      <p v-else-if="autoPrice">Complete travel details — your premium updates automatically.</p>
      <p v-else>Complete travel details, then tap <strong>Get price</strong>.</p>
    </div>

    <div v-if="summaryLines.length" class="live-breakdown">
      <div v-for="(line, i) in summaryLines" :key="i" class="live-row">
        <span>{{ line.label }}</span>
        <span class="live-row-value">{{ line.value }}</span>
      </div>
    </div>

    <div v-if="priceCalculated" class="premium-totals">
      <div class="premium-total-block">
        <span class="premium-total-label">Gross price</span>
        <span class="premium-total-amount">{{ formattedUsd }} <small>USD</small></span>
      </div>
      <div class="premium-total-block">
        <span class="premium-total-label">Amount (KSH)</span>
        <span class="premium-total-amount premium-total-amount--ksh">{{ kshAmount }} <small>KSH</small></span>
      </div>
      <p v-if="fxRate" class="fx-note">Conversion @ {{ fxRate }} USD/KSH</p>
    </div>

    <div v-if="hasQuoteRef && priceCalculated" class="sidebar-actions">
      <button type="button" class="sidebar-action-btn" @click="emailOpen = true">
        <v-icon x-small color="white" class="tw-mr-1">mdi-email-outline</v-icon>
        Email
      </button>
      <button type="button" class="sidebar-action-btn" @click="pdfDialogOpen = true">
        <v-icon x-small color="white" class="tw-mr-1">mdi-download</v-icon>
        PDF
      </button>
    </div>

    <div class="cta-bar">
      <button
        v-if="!autoPrice && !priceCalculated"
        type="button"
        class="btn-get-price"
        :disabled="!canGetPrice || pricing"
        @click="$emit('get-price')"
      >
        <v-icon v-if="!pricing" small color="#1E4276" class="tw-mr-1">mdi-calculator</v-icon>
        {{ pricing ? 'Calculating…' : 'Get price' }}
      </button>
      <button
        v-if="continueLabel && hasQuoteRef && priceCalculated"
        type="button"
        class="btn-continue"
        :disabled="syncing"
        @click="$emit('continue')"
      >
        {{ continueLabel }}
        <v-icon small color="white" class="tw-ml-1">mdi-arrow-right</v-icon>
      </button>
    </div>

    <v-dialog v-model="emailOpen" max-width="420" overlay-color="#1E4276" overlay-opacity="0.55">
      <div class="eq-dialog">
        <div class="eq-header">
          <div class="eq-header-icon"><v-icon size="24" color="white">mdi-email-send-outline</v-icon></div>
          <div>
            <p class="eq-title">Email quote</p>
            <p class="eq-subtitle">Send this travel quote to your customer</p>
          </div>
          <button type="button" class="eq-close" @click="emailOpen = false"><v-icon size="18" color="white">mdi-close</v-icon></button>
        </div>
        <div class="eq-body">
          <div class="eq-field">
            <label class="eq-label">Customer name</label>
            <input v-model="customerName" class="eq-input" placeholder="e.g. John Kamau" type="text">
          </div>
          <div class="eq-field">
            <label class="eq-label">Customer email <span class="eq-req">*</span></label>
            <input v-model="customerEmail" class="eq-input" placeholder="customer@email.com" type="email">
          </div>
        </div>
        <div class="eq-footer">
          <button type="button" class="eq-btn-cancel" @click="emailOpen = false">Cancel</button>
          <button type="button" class="eq-btn-send" :disabled="!customerEmail || sending" @click="sendEmail">
            {{ sending ? 'Sending…' : 'Send quote' }}
          </button>
        </div>
      </div>
    </v-dialog>

    <v-dialog v-model="pdfDialogOpen" max-width="420" overlay-color="#1E4276" overlay-opacity="0.55">
      <div class="eq-dialog">
        <div class="eq-header">
          <div class="eq-header-icon"><v-icon size="24" color="white">mdi-file-download-outline</v-icon></div>
          <div>
            <p class="eq-title">Download PDF quote</p>
            <p class="eq-subtitle">Formatted invoice — opens in a new tab</p>
          </div>
          <button type="button" class="eq-close" @click="pdfDialogOpen = false"><v-icon size="18" color="white">mdi-close</v-icon></button>
        </div>
        <div class="eq-body">
          <div class="eq-field">
            <label class="eq-label">Customer name on quote</label>
            <input v-model="pdfClientName" class="eq-input" placeholder="e.g. John Kamau" type="text">
          </div>
          <div class="eq-field">
            <label class="eq-label">Customer email</label>
            <input v-model="pdfClientEmail" class="eq-input" placeholder="customer@email.com" type="email">
          </div>
          <p class="eq-hint">PDF includes trip details, benefits, USD/KES premium and intermediary details.</p>
        </div>
        <div class="eq-footer">
          <button type="button" class="eq-btn-cancel" @click="pdfDialogOpen = false">Cancel</button>
          <button type="button" class="eq-btn-send" :disabled="generatingPdf" @click="downloadPdf">
            <v-progress-circular v-if="generatingPdf" indeterminate size="14" width="2" color="#1E4276" class="tw-mr-2" />
            {{ generatingPdf ? 'Generating…' : 'Open PDF' }}
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { usdToKsh } from '@/utils/travelPricing';
import { buildTravelQuoteEmailHtml, openTravelQuotePdf } from '@/utils/travelQuoteDocument';
import agentTravelApi from '@/services/agentTravelApi';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
  name: 'TravelQuoteSidebar',
  props: {
    grossUsd: { type: [String, Number], default: null },
    fxRate: { type: [String, Number], default: 130 },
    summaryLines: { type: Array, default: () => [] },
    quoteSnapshot: { type: Object, default: () => ({}) },
    priceCalculated: { type: Boolean, default: false },
    pricing: { type: Boolean, default: false },
    syncing: { type: Boolean, default: false },
    continueLabel: { type: String, default: 'Continue to sale' },
    gateReason: { type: String, default: '' },
    canGetPrice: { type: Boolean, default: false },
    /** Form is complete and a live price fetch is expected. */
    quoteReady: { type: Boolean, default: false },
    /** When true, premium is fetched automatically — no manual Get price button. */
    autoPrice: { type: Boolean, default: true },
  },
  data() {
    return {
      emailOpen: false,
      pdfDialogOpen: false,
      customerName: '',
      customerEmail: '',
      pdfClientName: '',
      pdfClientEmail: '',
      sending: false,
      generatingPdf: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
    agentName() {
      return this.authenticatedUser?.name || 'N/A';
    },
    hasQuoteRef() {
      return !!(this.quoteSnapshot && this.quoteSnapshot.quoteId);
    },
    formattedUsd() {
      const n = Number(this.grossUsd);
      if (Number.isNaN(n)) return this.grossUsd;
      return n.toFixed(2);
    },
    kshAmount() {
      if (!this.grossUsd) return '—';
      return usdToKsh(this.grossUsd, this.fxRate).toLocaleString();
    },
  },
  watch: {
    pdfDialogOpen(open) {
      if (open) {
        this.pdfClientName = this.customerName || this.quoteSnapshot.customerName || '';
        this.pdfClientEmail = this.customerEmail || this.quoteSnapshot.customerEmail || '';
      }
    },
  },
  methods: {
    buildQuotePayload() {
      return {
        ...this.quoteSnapshot,
        grossUsd: this.grossUsd,
        fxRate: this.fxRate,
        kshAmount: usdToKsh(this.grossUsd, this.fxRate),
        summaryLines: this.summaryLines,
      };
    },
    async downloadPdf() {
      this.generatingPdf = true;
      try {
        await openTravelQuotePdf(this.buildQuotePayload(), this.pdfClientName, {
          clientEmail: this.pdfClientEmail,
          agentName: this.agentName,
        });
        this.pdfDialogOpen = false;
        this.pdfClientName = '';
        this.pdfClientEmail = '';
      } catch (e) {
        this.$toast.error('Could not generate PDF');
      } finally {
        this.generatingPdf = false;
      }
    },
    async sendEmail() {
      const email = String(this.customerEmail || '').trim();
      if (!EMAIL_RE.test(email)) {
        this.$toast.error('Enter a valid email address');
        return;
      }
      if (!this.hasQuoteRef) {
        this.$toast.error('Quote is still saving — try again in a moment');
        return;
      }
      this.sending = true;
      try {
        const res = await agentTravelApi.emailTravelQuote({
          quoteId: this.quoteSnapshot.quoteId,
          emailAddress: email,
          name: this.customerName || 'Customer',
        });
        if (res.success) {
          this.$toast.success(`Quote emailed to ${email}`);
          this.emailOpen = false;
          return;
        }
        const body = buildTravelQuoteEmailHtml(this.buildQuotePayload(), this.customerName, {
          agentName: this.agentName,
        });
        await axios.post('/customer/digital-notification/client-api/v1/emails', {
          to: email,
          subject: `Travel Insurance Quote — ${this.quoteSnapshot.productLabel || 'Pacis Travel'}`,
          body,
        }, { _skipErrorRedirect: true });
        this.$toast.success(`Quote emailed to ${email}`);
        this.emailOpen = false;
      } catch (e) {
        this.$toast.error('Failed to send email');
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.quote-live-card {
  background: linear-gradient(135deg, #1E4276 0%, #2A5A9E 100%);
  color: white;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 8px 24px rgba(30, 66, 118, 0.22);
}

.card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
}

.card-title-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
}

.status-badge {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 3px 8px;
  border-radius: 20px;
  background: rgba(16, 185, 129, 0.25);
  color: #6EE7B7;
  border: 1px solid rgba(110, 231, 183, 0.35);
}

.status-badge--pending {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.55);
  border-color: rgba(255, 255, 255, 0.15);
}

.quote-placeholder {
  text-align: center;
  padding: 16px 8px 18px;
  opacity: 0.85;
}

.quote-placeholder p {
  font-size: 0.82rem;
  line-height: 1.45;
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.85);
}

.live-breakdown {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding-top: 12px;
  margin-bottom: 4px;
}

.live-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 7px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
}

.live-row-value {
  font-weight: 600;
  color: white;
  text-align: right;
  max-width: 58%;
  word-break: break-word;
}

.premium-totals {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding-top: 14px;
  margin-top: 8px;
  margin-bottom: 4px;
}

.premium-total-block {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 10px;
}

.premium-total-label {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.6);
}

.premium-total-amount {
  font-size: 1.35rem;
  font-weight: 800;
  color: #FFD700;
  line-height: 1.1;
}

.premium-total-amount small {
  font-size: 0.72rem;
  font-weight: 600;
  opacity: 0.8;
}

.premium-total-amount--ksh {
  font-size: 1.15rem;
  color: #7DD3FC;
}

.fx-note {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.45);
  margin: 0;
  text-align: right;
}

.sidebar-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 14px;
}

.sidebar-action-btn {
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border-radius: 10px;
  padding: 10px 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cta-bar {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.btn-get-price {
  width: 100%;
  border: none;
  border-radius: 50px;
  padding: 11px 14px;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #FFC107;
  color: #1E4276;
  box-shadow: 0 3px 10px rgba(255, 193, 7, 0.3);
}

.btn-get-price:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-continue {
  width: 100%;
  border: none;
  border-radius: 50px;
  padding: 11px 14px;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #00AEEF;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 174, 239, 0.35);
}

.btn-continue:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.eq-dialog { background: white; border-radius: 14px; overflow: hidden; }
.eq-header {
  display: flex; align-items: flex-start; gap: 12px; padding: 20px;
  background: linear-gradient(135deg, #1E4276, #2A5A9E); color: white;
}
.eq-header-icon {
  width: 44px; height: 44px; border-radius: 12px; background: rgba(255,255,255,0.15);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.eq-title { margin: 0; font-weight: 700; font-size: 1rem; }
.eq-subtitle { margin: 2px 0 0; font-size: 0.78rem; opacity: 0.75; }
.eq-close { margin-left: auto; background: none; border: none; cursor: pointer; opacity: 0.8; }
.eq-body { padding: 20px; }
.eq-field { margin-bottom: 14px; }
.eq-label { display: block; font-size: 0.78rem; font-weight: 600; color: #1E4276; margin-bottom: 6px; }
.eq-req { color: #DC2626; }
.eq-input {
  width: 100%; border: 1px solid #E2E8F0; border-radius: 8px; padding: 10px 12px;
  font-size: 0.9rem; color: #1E4276; box-sizing: border-box;
}
.eq-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 0 20px 20px; }
.eq-btn-cancel {
  border: 1px solid #E2E8F0; background: white; color: #64748B; border-radius: 8px;
  padding: 10px 16px; font-weight: 600; cursor: pointer;
}
.eq-btn-send {
  border: none; background: #00AEEF; color: white; border-radius: 8px;
  padding: 10px 18px; font-weight: 700; cursor: pointer;
}
.eq-btn-send:disabled { opacity: 0.5; cursor: not-allowed; }
.eq-hint { font-size: 0.75rem; color: #94a3b8; margin: 0; line-height: 1.4; }

@media (max-width: 600px) {
  .sidebar-actions {
    grid-template-columns: 1fr;
  }
  .sidebar-action-btn {
    min-height: 40px;
    font-size: 0.75rem;
  }
  .eq-footer {
    flex-direction: column-reverse;
    gap: 8px;
  }
  .eq-btn-cancel,
  .eq-btn-send {
    width: 100%;
    justify-content: center;
  }
}
</style>
